import { DivCardReward } from './div-card-reward';

export const DIV_CARD_REWARD_MULTIPLIERS = {
  corrupted: 0.5,
  foulborn: 0.75,
  sixLink: 1.5,
} as const;

export interface DivCardRewardUnitPriceMaps {
  currency: Map<string, number>;
  unique: Map<string, number>;
}

function uniqueModifier(reward: DivCardReward, multipliers = DIV_CARD_REWARD_MULTIPLIERS): number {
  if (reward.rarity !== 'unique') return 1;
  let modifier = 1;
  if (reward.corrupted) modifier *= multipliers.corrupted;
  if (reward.foulborn) modifier *= multipliers.foulborn;
  if (reward.sixLink) modifier *= multipliers.sixLink;
  return modifier;
}

function lookupUnitPrice(
  rewardName: string,
  rarity: DivCardReward['rarity'],
  maps: DivCardRewardUnitPriceMaps,
): number | null {
  if (rarity === 'currency') {
    return maps.currency.has(rewardName) ? maps.currency.get(rewardName)! : null;
  }
  if (rarity === 'unique') {
    return maps.unique.has(rewardName) ? maps.unique.get(rewardName)! : null;
  }
  return null;
}

// expected = (unitPrice × qty × flagModifiers) / stackSize
// Raw EV only — no nice-number snap, no unique-tier floor.
export function estimateDivCardRewardValue(
  reward: DivCardReward,
  maps: DivCardRewardUnitPriceMaps,
  multipliers = DIV_CARD_REWARD_MULTIPLIERS,
): number | null {
  if (reward.disabled || !reward.name) return null;
  if (reward.rarity !== 'currency' && reward.rarity !== 'unique') return null;
  if (!reward.stackSize || reward.stackSize <= 0) return null;

  const unitPrice = lookupUnitPrice(reward.name, reward.rarity, maps);
  if (unitPrice == null) return null;

  const qty = reward.quantity ?? 1;
  const modifier = uniqueModifier(reward, multipliers);
  return (unitPrice * qty * modifier) / reward.stackSize;
}
